import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { io } from "socket.io-client";
import { chatApi } from "../../apis/Chat.api";
import { serverUrl } from "../../config/apiConfig";
import { toast } from "react-toastify";

const SocketContext = createContext(null);

export const useSocket = () => {
  return useContext(SocketContext);
};

const SocketProvider = ({ children }) => {
  const [receiverUID, setReceiverUID] = useState();
  const [allMessages, setAllMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState([]);
  const [file, setFiles] = useState([]);
  const [preview, setPreview] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState();
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [orderId, setOrderId] = useState();

  const user = useSelector((state) => state.store.currentUser);
  const userId = user?._id;

  const socket = useMemo(() => {
    return io(serverUrl, {
      withCredentials: true,
      transports: ["websocket", "polling"],
    });
  }, []);

  useEffect(() => {
    if (userId) {
      socket.emit("joined", { user, userId });
      socket.emit("join-notification-room", { userId });
    }
  }, [socket, user, userId]);

  useEffect(() => {
    socket.on("notification", (notification) => {
      console.log("🔔 New Notification:", notification);
      toast.info(`${notification.title}: ${notification.message}`, {
        position: "top-right",
        autoClose: 5000,
      });
      setStatus((prev) => [...prev, notification]);
    });

    return () => {
      socket.off("notification");
    };
  }, [socket]);

  useEffect(() => {
    if (userId && receiverUID && orderId) {
      const payload = {
        senderId: userId,
        receiverId: receiverUID,
        orderId,
      };
      console.log("Emitting single-user-msgs with payload:", payload);
      socket.emit("single-user-msgs", payload);
    }
  }, [userId, receiverUID, orderId, socket]);

  useEffect(() => {
    socket.on("receive-message", (messagePayload) => {
      const { senderId, receiverId, message, timestamp } = messagePayload;
      console.log("Received message:", messagePayload);
      setAllMessages((prevMessages) => {
        // Avoid duplicates by checking if message already exists
        const messageExists = prevMessages.some(
          (msg) =>
            msg.sender_id?._id === senderId &&
            msg.receiver_id?._id === receiverId &&
            msg.message === message &&
            new Date(msg.created_at).getTime() === new Date(timestamp).getTime()
        );
        if (!messageExists) {
          return [
            ...prevMessages,
            {
              receiver_id: { _id: receiverId },
              sender_id: { _id: senderId },
              created_at: timestamp,
              message,
              order_id: orderId,
            },
          ];
        }
        return prevMessages;
      });
    });

    socket.on("user-all-messages", (msg) => {
      console.log("Received user-all-messages:", msg);
      setAllMessages(msg);
    });

    return () => {
      socket.off("receive-message");
      socket.off("user-all-messages");
    };
  }, [socket, orderId]);

  const uploadFiles = async (files, senderId, receiverId) => {
    setIsImageModalOpen(false);
    const formData = new FormData();

    Array.from(files).forEach((file) => {
      formData.append("files", file);
    });
    formData.append("receiverId", receiverId);
    formData.append("senderId", senderId);

    try {
      const response = await chatApi.uploadFiles(formData, {
        baseURL: serverUrl,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const progress = (progressEvent.loaded / progressEvent.total) * 100;
          console.log("file uploading progress is : ", progress);
          setProgress(progress);
        },
      });

      const filePaths = response.data.filePaths;

      return filePaths.map((item) => ({
        receiverId,
        senderId,
        filePath: item,
      }));
    } catch (error) {
      console.error("Error uploading files:", error);
      throw error;
    }
  };

  useEffect(() => {
  socket.on("notification", (data) => {
    console.log("🔔 Notification received:", data);

  toast.info(`${data.title}: ${data.message}`, {
        position: "top-right",
        autoClose: 5000,
      });
  });

  return () => {
    socket.off("notification");
  };
}, [socket]);


  // Message sending function

  const handleMessage = async () => {
    if (!userId || !receiverUID || !orderId) {
      console.log("Cannot send message, missing params:", { userId, receiverUID, orderId });
      return;
    }

    const senderId = userId;
    const receiverId = receiverUID;

    const newMessage = {
      senderId,
      receiverId,
      message,
      orderId,
    };

    try {
      if (file?.length > 0 && !message) {
        setIsLoading(true);
        setFiles([]);
        try {
          const newFileData = await uploadFiles(file, senderId, receiverId);
          newFileData.forEach((fileData) => {
            socket.emit("file-upload", fileData, orderId);
          });
        } catch (err) {
          console.log(err);
        } finally {
          setIsLoading(false);
          setPreview([]);
        }
      }
      if (message && file?.length <= 0) {
        socket.emit("message", newMessage);
        setMessage("");
      }

      if (file?.length > 0 && message) {
        setIsLoading(true);
        socket.emit("message", newMessage);
        setMessage("");
        setFiles([]);

        try {
          const newFileData = await uploadFiles(file, senderId, receiverId);
          newFileData.forEach((fileData) => {
            socket.emit("file-upload", fileData, orderId);
          });
        } catch (err) {
          console.log(err);
        } finally {
          setPreview([]);
          setIsLoading(false);
        }
      }
    } catch (error) {
      console.error("Error handling message:", error);
    }
  };

  return (
    <SocketContext.Provider
      value={{
        socket,
        setMessage,
        message,
        handleMessage,
        setReceiverUID,
        receiverUID,
        allMessages,
        status,
        setFiles,
        setPreview,
        isImageModalOpen,
        isLoading,
        progress,
        setIsImageModalOpen,
        file,
        preview,
        orderId,
        setOrderId,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export default SocketProvider;